import React, { useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import {
  Bold, Italic, Strikethrough, Heading1, Heading2, Heading3, List, ListOrdered,
  Quote, Code, Link2, Image as ImageIcon, Undo2, Redo2, Minus,
  AlignLeft, AlignCenter, AlignRight, Eraser,
} from 'lucide-react';
import UploadButton from './UploadButton';

export default function RichTextEditor({ value, onChange, placeholder = 'İçerik yazmaya başlayın...', minHeight = 280 }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'underline text-ink-600', rel: 'noopener noreferrer' } }),
      Image.configure({ inline: false, HTMLAttributes: { class: 'rounded-xl my-4 max-w-full' } }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Sync when external value changes (e.g. editing existing record)
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) editor.commands.setContent(value || '', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('Link URL', prev || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
  }, [editor]);

  const addImage = (url) => editor && editor.chain().focus().setImage({ src: url }).run();

  if (!editor) return null;

  return (
    <div className="border border-ink-100 rounded-xl bg-white overflow-hidden" data-testid="rich-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-ink-100 bg-cream-200/50 sticky top-0 z-10">
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Başlık 1"><Heading1 className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Başlık 2"><Heading2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Başlık 3"><Heading3 className="w-4 h-4" /></Btn>
        <Sep />
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Kalın (Ctrl+B)"><Bold className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Eğik (Ctrl+I)"><Italic className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Üstü çizili"><Strikethrough className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Kod satırı"><Code className="w-4 h-4" /></Btn>
        <Sep />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Madde listesi"><List className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numaralı liste"><ListOrdered className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Alıntı"><Quote className="w-4 h-4" /></Btn>
        <Sep />
        <Btn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Sola yasla"><AlignLeft className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Ortala"><AlignCenter className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Sağa yasla"><AlignRight className="w-4 h-4" /></Btn>
        <Sep />
        <Btn onClick={setLink} active={editor.isActive('link')} title="Bağlantı ekle"><Link2 className="w-4 h-4" /></Btn>
        <UploadButton
          onUploaded={addImage}
          tiny
          label={<span className="inline-flex items-center gap-1 text-xs"><ImageIcon className="w-4 h-4" /> Görsel</span>}
        />
        <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Yatay çizgi"><Minus className="w-4 h-4" /></Btn>
        <Sep />
        <Btn onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Biçimleri temizle"><Eraser className="w-4 h-4" /></Btn>
        <div className="flex-1" />
        <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Geri al (Ctrl+Z)"><Undo2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="İleri al (Ctrl+Shift+Z)"><Redo2 className="w-4 h-4" /></Btn>
      </div>

      {/* Editor content */}
      <EditorContent
        editor={editor}
        className="tiptap-editor px-4 py-3"
        style={{ minHeight }}
      />

      <style>{`
        .tiptap-editor .ProseMirror { outline: none; min-height: ${minHeight}px; font-size: 15px; line-height: 1.7; color: #1A2744; }
        .tiptap-editor .ProseMirror p.is-editor-empty:first-child::before { color: #a0a8b8; content: attr(data-placeholder); float: left; height: 0; pointer-events: none; }
        .tiptap-editor .ProseMirror h1 { font-family: 'Playfair Display', Georgia, serif; font-size: 2rem; line-height: 1.15; margin: 1.1em 0 0.4em; }
        .tiptap-editor .ProseMirror h2 { font-family: 'Playfair Display', Georgia, serif; font-size: 1.55rem; line-height: 1.2; margin: 1em 0 0.4em; }
        .tiptap-editor .ProseMirror h3 { font-family: 'Playfair Display', Georgia, serif; font-size: 1.2rem; margin: 0.9em 0 0.3em; }
        .tiptap-editor .ProseMirror p { margin: 0.6em 0; }
        .tiptap-editor .ProseMirror ul, .tiptap-editor .ProseMirror ol { padding-left: 1.6em; margin: 0.6em 0; }
        .tiptap-editor .ProseMirror ul li { list-style: disc; }
        .tiptap-editor .ProseMirror ol li { list-style: decimal; }
        .tiptap-editor .ProseMirror blockquote { border-left: 3px solid #C9A96E; padding-left: 1em; color: #374060; font-style: italic; margin: 0.8em 0; }
        .tiptap-editor .ProseMirror code { background: #F4EEE2; padding: 0.15em 0.35em; border-radius: 4px; font-size: 0.88em; }
        .tiptap-editor .ProseMirror a { color: #0F1E4D; text-decoration: underline; }
        .tiptap-editor .ProseMirror hr { border: 0; border-top: 1px solid #E8DECF; margin: 1.2em 0; }
        .tiptap-editor .ProseMirror img { border-radius: 0.75rem; max-width: 100%; }
      `}</style>
    </div>
  );
}

function Btn({ onClick, active, disabled, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`h-8 w-8 grid place-items-center rounded-md text-ink-500 hover:text-ink-700 hover:bg-white transition-colors ${active ? 'bg-white text-ink-700 shadow-sm' : ''} disabled:opacity-30 disabled:pointer-events-none`}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <div className="w-px h-5 bg-ink-100 mx-0.5" />;
}
